import { ReactNode, SyntheticEvent, useState } from 'react'
import { FileText, FlaskConical, Hash, IconComponent, Table2, Users } from '@digital-twins/design-system/icons'
import { LibraryItemKind } from '@digital-twins/shared-types'

import { LIBRARY_CARD_COVER_SCRIM_CLASS } from '@/components/library/libraryCardStyles'
import { cn } from '@/lib/utils'

export interface LibraryItemCoverProps {
  kind: LibraryItemKind
  /** Picks the generated cover's tone, so neighbours do not all share one colour (the item token). */
  seed: string
  imageUrl?: string | null
  /** Twin portraits: two or more draw a collage, one draws a single photo. */
  portraits?: Array<string>
  /**
   * Mixed covers for a collage (a collection's documents, studies and
   * portraits); wins over `portraits`. A document anchors top left, so a
   * slide's headline reads from its start instead of being cut mid-word.
   */
  images?: Array<LibraryCoverImage>
  /** Sizes the frame (an aspect ratio, a width). */
  className?: string
  /** Laid over the cover's bottom edge (chips). */
  overlay?: ReactNode
  /** The generated cover's test id is `{testId}-tint`; the photo collage's is `{testId}-portraits`. */
  testId: string
  /** `bold` deepens the generated cover's wash (a featured slot without an image). */
  tone?: CoverToneStrength
  /** Drawn instead of the generated cover when there is no image (a kind's own cover). */
  fallback?: ReactNode
}

export type CoverToneStrength = 'soft' | 'bold'

export interface LibraryCoverImage {
  src: string
  /** A document or dataset page (anchored top left); else a photo (anchored top). */
  isDocument?: boolean
}

interface CoverTone {
  /** The gradient wash, strongest top left. */
  wash: string
  /** A deeper wash for a featured slot. */
  boldWash: string
  /** A fine dot grid in the tone. */
  dots: string
  /** The faded kind mark. */
  ink: string
  /** A solid dot in the tone (a type accent on a chip). */
  swatch: string
}

/**
 * Semantic tones for generated covers, written out whole so the class
 * scanner sees them. Each reads on light, dark and brand themes because it
 * is a token at low alpha over the page background.
 */
const COVER_TONES: Array<CoverTone> = [
  {
    wash: 'bg-gradient-to-br from-primary/30 via-primary/10 to-primary/5',
    boldWash: 'bg-gradient-to-br from-primary/70 via-primary/35 to-primary/10',
    dots: 'bg-[radial-gradient(hsl(var(--primary)/0.22)_1px,transparent_1.5px)]',
    ink: 'text-primary',
    swatch: 'bg-primary',
  },
  {
    wash: 'bg-gradient-to-br from-chart-secondary/35 via-chart-secondary/10 to-primary/5',
    boldWash: 'bg-gradient-to-br from-chart-secondary/75 via-chart-secondary/35 to-primary/15',
    dots: 'bg-[radial-gradient(hsl(var(--chart-secondary)/0.25)_1px,transparent_1.5px)]',
    ink: 'text-chart-secondary',
    swatch: 'bg-chart-secondary',
  },
  {
    wash: 'bg-gradient-to-br from-chart-tertiary/35 via-chart-tertiary/10 to-primary/5',
    boldWash: 'bg-gradient-to-br from-chart-tertiary/75 via-chart-tertiary/35 to-primary/15',
    dots: 'bg-[radial-gradient(hsl(var(--chart-tertiary)/0.25)_1px,transparent_1.5px)]',
    ink: 'text-chart-tertiary',
    swatch: 'bg-chart-tertiary',
  },
  {
    wash: 'bg-gradient-to-br from-chart-accent/35 via-chart-accent/10 to-primary/5',
    boldWash: 'bg-gradient-to-br from-chart-accent/75 via-chart-accent/35 to-primary/15',
    dots: 'bg-[radial-gradient(hsl(var(--chart-accent)/0.25)_1px,transparent_1.5px)]',
    ink: 'text-chart-accent',
    swatch: 'bg-chart-accent',
  },
]

const KIND_ICONS: Record<LibraryItemKind, IconComponent> = {
  document: FileText,
  dataset: Table2,
  study: FlaskConical,
  audience: Users,
  topic: Hash,
}

/** A stable tone index for a seed (FNV-1a over the characters). */
export function coverToneIndex(seed: string): number {
  let hash = 0x811c9dc5
  for (let position = 0; position < seed.length; position += 1) {
    hash ^= seed.charCodeAt(position)
    hash = Math.imul(hash, 0x01000193)
  }
  return Math.abs(hash) % COVER_TONES.length
}

/** The tone classes for a seed; shared with banners that draw their own frame. */
export function coverTone(seed: string): CoverTone {
  return COVER_TONES[coverToneIndex(seed)]
}

interface GeneratedCoverProps {
  kind: LibraryItemKind
  seed: string
  testId: string
  tone?: CoverToneStrength
}

/** A token gradient, a dot grid and the kind's mark: never a grey box or a broken image. */
export function GeneratedCover(props: GeneratedCoverProps) {
  const { kind, seed, testId, tone: strength = 'soft' } = props

  const tone = coverTone(seed)
  const Icon = KIND_ICONS[kind]
  const isBold = strength === 'bold'

  return (
    <span
      className={cn('absolute inset-0 overflow-hidden', isBold ? tone.boldWash : tone.wash)}
      aria-hidden="true"
      data-testid={testId}
      data-tone={strength}
    >
      <span className={cn('absolute inset-0 bg-[length:14px_14px] opacity-70', tone.dots)} />
      <Icon
        className={cn(
          'absolute -bottom-6 -right-4 h-36 w-36 rotate-[-10deg] transition-transform duration-500 group-hover:rotate-[-6deg] group-hover:scale-105',
          isBold ? 'text-primary-dark opacity-[0.22]' : 'opacity-[0.16]',
          isBold ? null : tone.ink,
        )}
        strokeWidth={1.25}
      />
    </span>
  )
}

const IMAGE_CLASS =
  'h-full w-full object-cover transition-[transform,opacity] duration-500 ease-out group-hover:scale-[1.03]'

export interface FadeInImageProps {
  src: string
  className?: string
  onError?: () => void
}

/**
 * A cover image that fades in once it has loaded, over whatever the frame
 * draws beneath it (a token wash), so a slow image never leaves an empty
 * slab. An image already in the cache shows at once.
 */
export function FadeInImage(props: FadeInImageProps) {
  const { src, className, onError } = props

  const [loadedSrc, setLoadedSrc] = useState<string | null>(null)

  const isLoaded = loadedSrc === src

  const onLoad = (event: SyntheticEvent<HTMLImageElement>) => {
    setLoadedSrc(event.currentTarget.getAttribute('src'))
  }

  // A cached image can finish before React sees its load event.
  const onRef = (image: HTMLImageElement | null) => {
    if (image && image.complete && image.naturalWidth > 0 && loadedSrc !== src) {
      setLoadedSrc(src)
    }
  }

  return (
    <img
      ref={onRef}
      src={src}
      alt=""
      loading="lazy"
      decoding="async"
      referrerPolicy="no-referrer"
      className={cn(className, isLoaded ? 'opacity-100' : 'opacity-0')}
      data-loaded={isLoaded ? 'true' : 'false'}
      onLoad={onLoad}
      onError={onError}
    />
  )
}

export interface CoverPlaceholderProps {
  kind: LibraryItemKind
  seed: string
}

/** The token wash under a cover image while it loads: the generated cover's tone, without its mark. */
export function CoverPlaceholder(props: CoverPlaceholderProps) {
  const { kind, seed } = props

  const tone = coverTone(seed)
  const Icon = KIND_ICONS[kind]

  return (
    <span className={cn('absolute inset-0 flex items-center justify-center', tone.wash)} aria-hidden="true">
      <Icon className={cn('h-6 w-6 opacity-40', tone.ink)} strokeWidth={1.5} />
    </span>
  )
}

/** Where a cover image anchors in its frame. */
export function coverImageAnchorClass(isDocument: boolean | undefined): string {
  return isDocument ? 'object-left-top' : 'object-top'
}

/**
 * An item's cover in the Library card frame: a portrait collage (two or
 * more twins), a single image, or a generated token cover. An image that
 * fails to load drops out, so the reader never sees a broken image.
 */
export function LibraryItemCover(props: LibraryItemCoverProps) {
  const { kind, seed, imageUrl, portraits = [], images, className, overlay, testId, tone = 'soft', fallback } = props

  const [failed, setFailed] = useState<Array<string>>([])

  const onImageError = (src: string) => {
    setFailed((previous) => (previous.includes(src) ? previous : [...previous, src]))
  }

  const collageSources: Array<LibraryCoverImage> = images ?? portraits.map((src) => ({ src }))
  const usablePortraits = collageSources.filter((image) => !failed.includes(image.src)).slice(0, 3)
  const isDocumentKind = kind === 'document' || kind === 'dataset'
  const single: LibraryCoverImage | null =
    usablePortraits[0] ?? (imageUrl && !failed.includes(imageUrl) ? { src: imageUrl, isDocument: isDocumentKind } : null)

  let body: ReactNode
  if (usablePortraits.length >= 2) {
    body = (
      <span
        className={cn(
          'absolute inset-0 grid gap-0.5',
          usablePortraits.length >= 3 ? 'grid-cols-[2fr_1fr] grid-rows-2' : 'grid-cols-2',
        )}
        data-testid={`${testId}-portraits`}
      >
        {usablePortraits.map((image, position) => (
          <span
            key={image.src}
            className={cn('relative overflow-hidden', usablePortraits.length >= 3 && position === 0 ? 'row-span-2' : '')}
          >
            <CoverPlaceholder kind={kind} seed={`${seed}-${position}`} />
            <FadeInImage
              src={image.src}
              className={cn('relative', IMAGE_CLASS, coverImageAnchorClass(image.isDocument))}
              onError={() => onImageError(image.src)}
            />
          </span>
        ))}
      </span>
    )
  } else if (single) {
    body = (
      <>
        <CoverPlaceholder kind={kind} seed={seed} />
        <FadeInImage
          src={single.src}
          className={cn('absolute inset-0', IMAGE_CLASS, coverImageAnchorClass(single.isDocument))}
          onError={() => onImageError(single.src)}
        />
      </>
    )
  } else {
    body = fallback ?? <GeneratedCover kind={kind} seed={seed} testId={`${testId}-tint`} tone={tone} />
  }

  const hasImage = usablePortraits.length >= 2 || single != null

  return (
    <div className={cn('relative w-full overflow-hidden rounded-[14px] bg-primary/5', className)}>
      {body}
      {/* A calm base under the chips, so they read on a busy or white page. */}
      {overlay && hasImage ? (
        <span
          aria-hidden="true"
          className={LIBRARY_CARD_COVER_SCRIM_CLASS}
          data-testid={`${testId}-scrim`}
        />
      ) : null}
      {overlay ? (
        <div className="absolute inset-x-3 bottom-3 flex min-w-0 items-center gap-1.5 overflow-hidden">{overlay}</div>
      ) : null}
    </div>
  )
}

export interface CoverAvatarStackProps {
  /** Portrait URLs; the first three that load are drawn. */
  portraits: Array<string>
  testId: string
}

const AVATAR_STACK_MAX = 3

/** Up to three overlapping round portraits, for a cover corner (a study's twins). */
export function CoverAvatarStack(props: CoverAvatarStackProps) {
  const { portraits, testId } = props

  const [failed, setFailed] = useState<Array<string>>([])

  const shown = portraits.filter((src) => !failed.includes(src)).slice(0, AVATAR_STACK_MAX)

  if (shown.length === 0) {
    return null
  }

  return (
    <span className="ml-auto flex shrink-0 items-center -space-x-2" aria-hidden="true" data-testid={testId}>
      {shown.map((src) => (
        <img
          key={src}
          src={src}
          alt=""
          loading="lazy"
          decoding="async"
          className="h-7 w-7 rounded-full object-cover object-top shadow-sm ring-2 ring-background"
          onError={() => setFailed((previous) => (previous.includes(src) ? previous : [...previous, src]))}
        />
      ))}
    </span>
  )
}
